import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from '../../components/SeoMeta';
import CmsImage from '../../components/CmsImage';
import Lightbox from '../../components/Lightbox';
import { fetchLocalizedCollection } from '../../lib/cms';

export default function AboutPageEn() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [milestones, setMilestones] = useState([]);
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    fetchLocalizedCollection('sider', 'en').then((items) => {
      const getSlugVal = (it) =>
        typeof it?.slug === 'object'
          ? it.slug.da || it.slug._ || it.slug.en || Object.values(it.slug)[0]
          : it?.slug;
      const match = (items || []).find(
        (s) => getSlugVal(s) === 'om-os' || getSlugVal(s) === 'about'
      );
      if (match) setPageData(match);
    });

    fetchLocalizedCollection('historiske_milepaele', 'en').then((items) => {
      if (items && items.length) {
        const sorted = items.sort((a, b) => (a.sortering || 0) - (b.sortering || 0));
        setMilestones(
          sorted.map((m) => {
            const rawDesc = m.beskrivelse || '';
            const plainDesc = rawDesc.replace(/<[^>]+>/g, '').trim();
            const img = Array.isArray(m.billede) ? m.billede[0] : m.billede;
            return {
              year: m.aarstal,
              title: m.titel,
              desc: plainDesc || rawDesc,
              img: img || '',
            };
          })
        );
      }
    });
  }, []);

  const historicalMilestones = [
    {
      year: '1921',
      title: 'The factory workers gathered in front of the workshop',
      desc: 'In front of the white building with the characteristic arched gates, the factory staff gathers. Foremen, journeymen, and apprentices stand side by side – young boys embarking on their trade alongside craftsmen with decades of manual expertise. The photograph radiates community, hard work, and professional pride, standing as a testament to the people who laid the cornerstone of PMB.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/medarbejdere-1921.webp',
    },
    {
      year: '1943',
      title: 'Machinists and overhead belt drives in the main hall',
      desc: 'A glance into the mechanical assembly hall, where overhead belt-drive systems from the ceiling power presses, lathes, and milling machines. The atmosphere is focused and energetic, capturing the bustling daily work of operators in overalls running industrial machinery. It was during this period that the factory consolidated its industrial identity and established the foundation for future growth.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/maskinarbejdere-pmb-1943.webp',
    },
    {
      year: '1930',
      title: 'Milestone: Celebrating press number 1,000',
      desc: 'This historic photograph marks a significant production milestone: machine press number 1,000. Workers, engineers, management, and guests gathered around the finished machine in celebration. For the company, this symbolized sustained growth, uncompromising build quality, and a breakthrough machine that left an enduring mark on Nordic manufacturing.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/ekscenterpresser-1930-presse-nummer-1000.webp',
    },
    {
      year: '1965',
      title: 'Fully industrialized, modern machine manufacturing',
      desc: 'Stepping forward into the mid-1960s, the factory transitioned into a modern manufacturing facility. Heavy-duty presses stood in orderly lines under high ceilings and bright industrial lighting. This marked the transformation from workshop craftsmanship to a fully scaled industrial operation capable of competing internationally with robust eccentric presses.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/pressere-1965.webp',
    },
    {
      year: "1930's",
      title: 'Innovation: Early car lifts and the Ford Model T',
      desc: 'While eccentric presses became our hallmark, Povl Møllers Maskinfabrik continuously engineered creative mechanical solutions for industry. Among the more notable achievements were hydraulic car lifts designed to elevate full-sized vehicles – pictured here with an early Ford. A clear symbol of the ingenuity and technical problem-solving that has always characterized PMB.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/ford-t-og-autolifte-fra-pmb.webp',
    },
  ];

  const displayedMilestones = milestones.length > 0 ? milestones : historicalMilestones;

  const title = pageData?.titel || 'Once upon a time...';
  const badge = pageData?.undertitel || 'Our Heritage';
  const intro =
    pageData?.intro ||
    'From workshop to machine factory since 1921. Discover the heritage and craftsmanship of Povl Møllers Maskinfabrik in Borup.';
  const seoTitle =
    pageData?.seo_titel || 'PMB History – From Workshop to Machine Factory since 1921';
  const metaDesc =
    pageData?.meta_beskrivelse ||
    'PMB History – From local workshop to machine factory since 1921. Discover the heritage and craftsmanship of Povl Møllers Maskinfabrik in Borup.';

  return (
    <>
      <SeoMeta
        title={seoTitle}
        description={metaDesc}
      />

      {/* Breadcrumb */}
      <div className="bg-stone-100 border-b border-stone-200 py-3 px-4 sm:px-6 lg:px-8 text-xs text-stone-600">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/en" className="hover:text-[#006368] transition">
            Home
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="text-stone-500">About us</span>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="font-semibold text-stone-900">{title}</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#1a2024] text-white py-16 sm:py-24 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 rounded bg-[#006368] text-white text-xs font-bold uppercase tracking-wider mb-4">
              {badge}
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              {title}
            </h1>
            <p className="mt-4 text-lg sm:text-xl text-stone-300 leading-relaxed">
              <strong>Povl Møllers Maskinfabrik</strong> began as a modest workshop in the early 1900s. Craftsmanship was at the heart of everything, driven by overhead belt systems, muscle power, and deep professional dedication.
            </p>
            <p className="mt-3 text-sm sm:text-base text-stone-400 leading-relaxed">
              Over time, the factory expanded – both in workforce and technological capability – earning international recognition for durable, dependable eccentric presses exported worldwide.
            </p>
          </div>
        </div>
      </section>

      {/* Historical Milestones Timeline */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24">
          {pageData?.indhold && (
            <div className="max-w-4xl mx-auto p-8 rounded-xl bg-stone-50 border border-stone-200">
              <div
                className="cms-content text-stone-700 text-base sm:text-lg leading-relaxed space-y-4 [&_p]:mb-4"
                dangerouslySetInnerHTML={{ __html: pageData.indhold }}
              />
            </div>
          )}
          {displayedMilestones.map((item, index) => {
            const isEven = index % 2 === 0;
            return (
              <div
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                  isEven ? '' : 'lg:flex-row-reverse'
                }`}
              >
                {/* Text description */}
                <div className={`lg:col-span-6 ${isEven ? 'lg:order-1' : 'lg:order-2'} space-y-4`}>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-stone-100 text-[#006368] text-xs font-bold uppercase tracking-wider">
                    <i className="fa-solid fa-clock-rotate-left text-xs" />
                    <span>Circa {item.year}</span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
                    {item.title}
                  </h2>
                  <p className="text-stone-700 text-base sm:text-lg leading-relaxed">
                    {item.desc}
                  </p>
                </div>

                {/* Historical photo with interactive lightbox trigger */}
                <div className={`lg:col-span-6 ${isEven ? 'lg:order-2' : 'lg:order-1'}`}>
                  <div
                    onClick={() => {
                      setLightboxIndex(index);
                      setLightboxOpen(true);
                    }}
                    className="group relative cursor-pointer overflow-hidden rounded-xl border border-stone-200 shadow-md bg-stone-100"
                  >
                    <CmsImage
                      src={item.img}
                      alt={item.title}
                      className="w-full h-80 sm:h-96 object-cover group-hover:scale-105 transition duration-500"
                      width={800}
                    />
                    <div className="absolute inset-0 bg-[#006368]/20 opacity-0 group-hover:opacity-100 transition duration-300 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-white/90 text-[#006368] flex items-center justify-center shadow-lg">
                        <i className="fa-solid fa-magnifying-glass text-base" />
                      </div>
                    </div>
                    <div className="absolute bottom-3 right-3 px-3 py-1 rounded bg-stone-900/80 text-white text-xs font-medium backdrop-blur-xs">
                      Click to inspect
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Summary Outro CTA */}
      <section className="py-20 bg-stone-50 border-t border-stone-200">
        <div className="max-w-4xl mx-auto px-4 text-center space-y-6">
          <span className="text-xs font-bold uppercase tracking-wider text-[#006368]">
            Living Legacy
          </span>
          <h2 className="text-3xl font-extrabold text-stone-900">
            More than a century of experience working for your production
          </h2>
          <p className="text-stone-700 text-base sm:text-lg leading-relaxed">
            The machines have evolved, and computers now control hydraulic circuits and laser beams. But the pride in our craft, the thoroughness, and the desire to build lasting machinery remains exactly the same as in 1921.
          </p>
          <div className="pt-4 flex flex-wrap justify-center gap-4">
            <Link
              to="/en/contact"
              className="px-6 py-3 rounded bg-[#006368] hover:bg-[#004d51] text-white font-semibold text-sm transition shadow-sm"
            >
              Get in touch with us
            </Link>
            <Link
              to="/en/suppliers"
              className="px-6 py-3 rounded bg-white hover:bg-stone-100 text-stone-800 font-semibold text-sm border border-stone-300 transition"
            >
              Explore our suppliers
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <Lightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={displayedMilestones.map((m) => ({ url: m.img, title: m.title }))}
        currentIndex={lightboxIndex}
        setCurrentIndex={setLightboxIndex}
      />
    </>
  );
}
